import { useContext, useEffect, useState } from "react";
import SettingsContext from "../../../contexts/SettingsContext";
import UserContext from "../../../contexts/UserContext";
import createIronSightsTarget from "./IronSights";
import createDuelsTarget from "./Duels";
import { addDoc, collection, Firestore } from "firebase/firestore";
import { User } from "firebase/auth";
import Background from "../Background/Background";

// prop types
type DisplayProps = {
  ironsights: boolean;
  duels: boolean;
};

/**
 * Display a duels or iron sights game
 * @param props is the props for the game
 * @returns the game
 */
export default function DisplayTrainer(props: DisplayProps) {
  const settingsContext = useContext(SettingsContext);
  const userContext = useContext(UserContext);

  const [done, setDone] = useState(false); // whether the game is done
  const [score, setScore] = useState(0); // the score of the current game
  const [clicks, setClicks] = useState(0); // the number of clicks
  const [distSum, setDistSum] = useState(0); // the sum of distances from target (for accuracy)
  const difficulties = ["Easy", "Medium", "Hard", "Custom"]; // the different difficulties
  const [difficulty, setDifficulty] = useState<string | null>(null); // the current difficulty

  const [keyIdx, setKeyIdx] = useState(0); // the key of the current target
  const [misses, setMisses] = useState(0); // the number of misses
  const [target, setTarget] = useState(<></>); // the current target

  // iron sights
  const [targetVel, setTargetVel] = useState(0); // the target's velocity
  const [targetAcc, setTargetAcc] = useState(0); // the target's acceleration
  const [leftBound, setLeftBound] = useState(0); // the screen's left bound
  const [rightBound, setRightBound] = useState(0); // the screen's right bound

  // duels
  const [duration, setDuration] = useState(0); // the target's duration

  /**
   * Reset a target (parameters are optional if they have just been changed since (setState is async))
   * @param id is the key of the target
   * @param dur is the duration of the target
   * @param tVel is the velocity of the target
   * @param tAcc is the acceleration of the target
   * @param lBound is the left bound of the target
   * @param rBound is the right bound of the target
   */
  const resetTarget = (
    id = keyIdx,
    dur = duration,
    tVel = targetVel,
    tAcc = targetAcc,
    lBound = leftBound,
    rBound = rightBound
  ) => {
    setTarget(
      props.ironsights
        ? createIronSightsTarget(
            settingsContext,
            id + 1,
            lBound,
            rBound,
            setScore,
            setMisses,
            setClicks,
            setDistSum,
            tAcc,
            tVel,
            true
          )
        : createDuelsTarget(
            settingsContext,
            id + 1,
            setScore,
            setMisses,
            setClicks,
            setDistSum,
            dur
          )
    );
  };

  // Create a new target if the score or misses change (but not at the beginning when both are 0)
  useEffect(() => {
    if (misses !== 0 || score !== 0) {
      const id = keyIdx + 1;
      setKeyIdx(id);
      resetTarget(id);
    }
  }, [score, misses]);

  // when misses change, check if game should be done and if so update db
  useEffect(() => {
    if (misses >= 5) {
      setDone(true);
      let db: Firestore | null | undefined = userContext.db;
      let user: User | null | undefined = userContext.user;

      if (db && user) {
        addDoc(collection(db, "rounds"), {
          userID: user.uid,
          timestamp: Date.now(),
          type: props.ironsights ? "Iron Sights" : "Duels",
          precision: clicks > 0 ? Math.ceil((score / clicks) * 100) / 100 : 0,
          accuracy: clicks > 0 ? Math.ceil((distSum / clicks) * 100) / 100 : 0,
          difficulty: difficulty,
          score: score,
        }).catch((e) => {
          console.error("Error adding document: ", e);
        });
      }
    }
  }, [misses]);

  /**
   * Click on a difficulty
   * @param difficulty is the new difficulty
   */
  const clickDifficulty = (difficulty: string) => {
    setDifficulty(difficulty);
    const id = 0;
    setKeyIdx(id);
    const dur =
      difficulty === "Easy"
        ? 3000
        : difficulty === "Medium"
        ? 2000
        : difficulty === "Hard"
        ? 1500
        : settingsContext.duration
        ? settingsContext.duration
        : 2000;
    setDuration(dur);
    const tVel =
      difficulty === "Easy"
        ? 0.19
        : difficulty === "Medium"
        ? 0.23
        : difficulty === "Hard"
        ? 0.27
        : settingsContext.targetVel
        ? settingsContext.targetVel
        : 0.23;
    setTargetVel(tVel);
    const tAcc =
      difficulty === "Easy"
        ? -0.004
        : difficulty === "Medium"
        ? -0.0045
        : difficulty === "Hard"
        ? -0.005
        : settingsContext.targetAcc
        ? settingsContext.targetAcc
        : -0.0045;
    setTargetAcc(tAcc);
    const lBound =
      difficulty === "Easy"
        ? -8
        : difficulty === "Medium"
        ? -11
        : difficulty === "Hard"
        ? -15
        : -11;
    setLeftBound(lBound);
    const rBound =
      difficulty === "Easy"
        ? 8
        : difficulty === "Medium"
        ? 11
        : difficulty === "Hard"
        ? 15
        : 11;
    setRightBound(rBound);
    resetTarget(id, dur, tVel, tAcc, lBound, rBound);
    setScore(0);
    setMisses(0);
    setClicks(0);
    setDone(false);
  };

  // Renders the options for the different difficulties
  const difficultyOptions = (
    <div className="flex gap-4 justify-between flex-wrap">
      {difficulties.map((difficulty) => (
        <button
          className="w-24 px-2 py-3 text-md font-medium leading-5 text-center capitalize text-gray-800 bg-gray-200 rounded-lg hover:bg-gray-100 focus:outline-none"
          key={difficulty}
          onClick={() => clickDifficulty(difficulty)}
        >
          {difficulty}
        </button>
      ))}
    </div>
  );

  // Give information about the current game mode
  const abouts = [
    {
      title: "Iron Sights",
      description:
        "Shoot moving targets by clicking at the right time and place. If you miss 5 times, the round will end. Choose a difficulty below to get started (a custom difficulty will use the settings defined in the settings page).",
    },
    {
      title: "Duels",
      description:
        "Shooting static targets by clicking. After a certain amount of time, a target will disappear and a new one will show up. If you are unable to hit 5 targets before they disappear, the round will end. Choose a difficulty below to get started (a custom difficulty will use the settings defined in the settings page).",
    },
  ];
  // Information to use
  const about = props.ironsights ? abouts[0] : abouts[1];

  return (
    <Background
      outer={
        <>
          {!difficulty && (
            <div className="w-full h-full flex justify-center items-center">
              <div className="flex flex-col bg-indigo-500 p-4 m-10 rounded-md">
                <div className="pb-4 max-w-md">
                  <p className="text-white font-semibold text-4xl text-center pb-2">
                    Welcome to {about.title}
                  </p>
                  <p className="text-gray-200 text-lg">{about.description}</p>
                </div>
                {difficultyOptions}
              </div>
            </div>
          )}
          {done && (
            <div className="w-full h-full flex justify-center items-center">
              <div className="flex flex-col bg-indigo-500 p-4 m-10 rounded-md">
                <div className="pb-4">
                  <p className="text-white font-semibold text-4xl text-center pb-2">
                    {"Score: " + score}
                  </p>
                  <p className="text-gray-200 text-lg">
                    Try again or choose a different difficulty.
                  </p>
                </div>
                <div className="flex justify-center items-center flex-col">
                  <button
                    className="w-24 px-2 py-3 text-md font-medium leading-5 text-center capitalizerounded-lg text-gray-800 bg-gray-200 rounded-lg hover:bg-gray-100 focus:outline-none"
                    key={difficulty}
                    onClick={() => clickDifficulty(difficulty as string)}
                  >
                    <span className="flex items-center gap-1 justify-center">
                      Retry
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6 inline"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                    </span>
                  </button>
                  <p className="text-white font-semibold text-2xl my-4">or</p>
                </div>
                {difficultyOptions}
              </div>
            </div>
          )}
          {difficulty && !done && (
            <div className="flex px-6 py-2 justify-end gap-4">
              <p className="text-white text-2xl font-semibold">
                Score: {score}
              </p>
              <p className="text-white text-2xl font-semibold">
                Lives: {5 - misses}
              </p>
            </div>
          )}
        </>
      }
      inner={!done && difficulty ? target : false}
      lives={false}
    />
  );
}
